/**
 * Canonical business brief schema and validation.
 *
 * This is the single source of truth for what fields a business brief can
 * contain and what values are valid for select fields. The schema reflects
 * what Typeform actually sends today — option lists must be updated here
 * whenever the Typeform is changed.
 *
 * The dashboard's `brief-schema.ts` mirrors this for UI rendering but
 * the API validates against THIS file.
 */
import { isAcceptedTimezone } from './business-brief-timezones.js';
// ── Industry constants ───────────────────────────────────────────────
const NON_ECOMMERCE_INDUSTRIES = [
    'Coach/Consultant',
    'Professional Service',
    'Local Service',
    'Marketing Agency',
    'Med Spa',
    'Real Estate',
];
// ── Schema definition ────────────────────────────────────────────────
export const BUSINESS_BRIEF_SCHEMA = [
    { id: 'company', fieldType: 'short_text', required: true },
    // Industry + sub-niche
    {
        id: 'industry',
        fieldType: 'single_select',
        required: true,
        options: [
            'Coach/Consultant',
            'Professional Service',
            'Local Service',
            'Marketing Agency',
            'Med Spa',
            'Real Estate',
            'eCommerce',
        ],
    },
    {
        id: 'subNiche_coach',
        fieldType: 'single_select',
        required: true,
        showWhen: { questionId: 'industry', equals: 'Coach/Consultant' },
        // Typeform sends free-text niches — we accept any non-empty string
    },
    {
        id: 'subNiche_professional',
        fieldType: 'single_select',
        required: true,
        showWhen: { questionId: 'industry', equals: 'Professional Service' },
    },
    {
        id: 'subNiche_local',
        fieldType: 'single_select',
        required: true,
        showWhen: { questionId: 'industry', equals: 'Local Service' },
    },
    {
        id: 'subNiche_agency',
        fieldType: 'single_select',
        required: true,
        showWhen: { questionId: 'industry', equals: 'Marketing Agency' },
    },
    {
        id: 'subNiche_realestate',
        fieldType: 'single_select',
        required: true,
        showWhen: { questionId: 'industry', equals: 'Real Estate' },
    },
    {
        id: 'subNiche_ecommerce',
        fieldType: 'single_select',
        required: true,
        showWhen: { questionId: 'industry', equals: 'eCommerce' },
    },
    // Revenue
    {
        id: 'monthlyRevenue',
        fieldType: 'single_select',
        required: true,
        options: [
            'Pre Revenue',
            '$1K - $2K / month',
            '$2K - $5K / month',
            '$5K - $8K / month',
            '$8K - $10K / month',
            '$10K - $20K / month',
            '$20K - $40K / month',
            '$40K - $60K / month',
            '$60K - $80K / month',
            '$80K - $100K / month',
            '$100K - $200K / month',
            '$200K - $500K / month',
            '$500K / month +',
            // Legacy values from old Typeform
            'Pre-Revenue',
            '$0 - $1K',
            '$1K - $5K',
            '$5K - $10K',
            '$10K - $25K',
            '$25K - $50K',
            '$50K - $75K',
            '$75K - $100K',
            '$100K - $150K',
            '$150K - $250K',
            '$250K - $350K',
            '$350K - $500K',
            '$500K+',
        ],
    },
    // Digital assets
    { id: 'website', fieldType: 'short_text', required: false },
    { id: 'socialMedia', fieldType: 'short_text', required: false },
    { id: 'otherLinks', fieldType: 'short_text', required: false },
    // About your business
    { id: 'businessStatement', fieldType: 'long_text', required: true },
    /**
     * Bot-parity: bot's Typeform asks "Who is your Founder's target audience?"
     * separately from the business statement and threads it into all 4 research
     * arms + plan-themes. We capture it here so calendar prompts can address
     * the audience explicitly instead of inferring it from prose.
     *
     * Tolerated empty for legacy briefs — write-captions falls back to
     * extracting audience from `businessStatement` when this is blank.
     */
    { id: 'targetAudience', fieldType: 'long_text', required: false },
    /**
     * Bot-parity: bot's Typeform asks "Is your Founder's business local or
     * global?" and folds it into the Brand bundle for research prompts.
     * Captures whether geo-grounding (city / region) is relevant for hooks
     * and competitor scoping.
     */
    {
        id: 'businessLocality',
        fieldType: 'single_select',
        required: false,
        options: ['Local', 'Global', 'Both'],
    },
    { id: 'top3Services', fieldType: 'long_text', required: true },
    {
        id: 'teamSize',
        fieldType: 'single_select',
        required: true,
        options: [
            // Current Typeform options
            '0 (Just me!)',
            '1-5',
            '5-10',
            '10-20',
            '20-50',
            '50-100',
            '100+',
            // Legacy options
            'Just me',
            '2-3',
            '4-5',
            '6-10',
            '11-25',
            '26-50',
            '50+',
        ],
    },
    {
        id: 'whyDoer',
        fieldType: 'long_text', // Typeform sends free-text for most entries
        required: true,
    },
    { id: 'whyDoneverse', fieldType: 'long_text', required: false },
    // Outsourcing adventure
    {
        id: 'timezone',
        fieldType: 'single_select',
        required: true,
        // Curated abbreviations + full IANA database + legacy Typeform values.
        // Validated via predicate rather than an enumerated list (IANA set varies
        // by runtime tzdata).
        validateValue: isAcceptedTimezone,
    },
    {
        id: 'blueprintPriority',
        fieldType: 'rank',
        required: true,
        options: [
            'Attract Traffic',
            'Capture Leads',
            'Nurture Prospects',
            'Convert Sales',
            'Deliver & WOW',
            'Upsell & Retain',
            'Get Referrals',
        ],
    },
    {
        id: 'project30Days_nonEcomm',
        fieldType: 'long_text', // Typeform sends free-text for most entries
        required: true,
        showWhen: { questionId: 'industry', equals: NON_ECOMMERCE_INDUSTRIES },
    },
    {
        id: 'project30Days_ecomm',
        fieldType: 'long_text',
        required: true,
        showWhen: { questionId: 'industry', equals: 'eCommerce' },
    },
    { id: 'project12Months', fieldType: 'long_text', required: true },
    {
        id: 'tasksToOffload',
        fieldType: 'long_text', // Typeform sends as string, not array
        required: true,
    },
    // Branding
    {
        id: 'hasBrandGuide',
        fieldType: 'single_select',
        required: true,
        options: ['Yes', 'No'],
    },
    {
        id: 'brandGuideLink',
        fieldType: 'short_text',
        required: true,
        showWhen: { questionId: 'hasBrandGuide', equals: 'Yes' },
    },
    { id: 'logoLink', fieldType: 'short_text', required: false },
    { id: 'brandColors', fieldType: 'short_text', required: false },
    { id: 'brandFonts', fieldType: 'short_text', required: false },
    { id: 'brandImages', fieldType: 'short_text', required: false },
    { id: 'brandGuidelines', fieldType: 'short_text', required: false },
    // Doer training badges — Typeform sends free-text labels, no fixed options
    {
        id: 'additionalBadges_nonEcomm',
        fieldType: 'multi_select',
        required: false,
        showWhen: { questionId: 'industry', equals: NON_ECOMMERCE_INDUSTRIES },
    },
    {
        id: 'additionalBadges_ecomm',
        fieldType: 'multi_select',
        required: false,
        showWhen: { questionId: 'industry', equals: 'eCommerce' },
    },
    // Tools — Typeform sends free-text, no fixed options
    {
        id: 'tool_email_nonEcomm',
        fieldType: 'short_text',
        required: false,
        showWhen: { questionId: 'industry', equals: NON_ECOMMERCE_INDUSTRIES },
    },
    {
        id: 'tool_landingPage_nonEcomm',
        fieldType: 'short_text',
        required: false,
        showWhen: { questionId: 'industry', equals: NON_ECOMMERCE_INDUSTRIES },
    },
    {
        id: 'tool_website_nonEcomm',
        fieldType: 'short_text',
        required: false,
        showWhen: { questionId: 'industry', equals: NON_ECOMMERCE_INDUSTRIES },
    },
    {
        id: 'tool_website_ecomm',
        fieldType: 'short_text',
        required: false,
        showWhen: { questionId: 'industry', equals: 'eCommerce' },
    },
    {
        id: 'tool_inventory_ecomm',
        fieldType: 'short_text',
        required: false,
        showWhen: { questionId: 'industry', equals: 'eCommerce' },
    },
    {
        id: 'tool_email_ecomm',
        fieldType: 'short_text',
        required: false,
        showWhen: { questionId: 'industry', equals: 'eCommerce' },
    },
];
// ── Known field IDs (for detecting unknown keys) ─────────────────────
const KNOWN_FIELD_IDS = new Set(BUSINESS_BRIEF_SCHEMA.map((f) => f.id));
// Also allow internal/legacy keys
const ALLOWED_EXTRA_KEYS = new Set(['_raw', '_legacy']);
/**
 * Check if a field is visible given current answers (showWhen logic).
 */
function isVisible(showWhen, data) {
    if (!showWhen)
        return true;
    const depValue = data[showWhen.questionId];
    const targets = Array.isArray(showWhen.equals) ? showWhen.equals : [showWhen.equals];
    if (Array.isArray(depValue)) {
        return targets.some((t) => depValue.includes(t));
    }
    return targets.includes(depValue);
}
/**
 * Validate a business brief data object against the canonical schema.
 *
 * Always lenient:
 * - Unknown fields produce warnings, not hard failures
 * - Missing optional fields are fine
 * - Free-text fields (no options) just need to be non-empty if required
 * - Select fields with options must match if options are defined
 */
export function validateBusinessBrief(data) {
    const issues = [];
    // Check for unknown keys (warning only)
    for (const key of Object.keys(data)) {
        if (!KNOWN_FIELD_IDS.has(key) && !ALLOWED_EXTRA_KEYS.has(key)) {
            issues.push({ field: key, message: 'Unknown field' });
        }
    }
    for (const field of BUSINESS_BRIEF_SCHEMA) {
        const value = data[field.id];
        const visible = isVisible(field.showWhen, data);
        // Required check — only for visible fields
        if (field.required && visible) {
            const isEmpty = value === undefined ||
                value === null ||
                value === '' ||
                (Array.isArray(value) && value.length === 0);
            if (isEmpty) {
                issues.push({ field: field.id, message: 'Required field is missing' });
                continue;
            }
        }
        // Skip further checks if value is absent
        if (value === undefined || value === null || value === '')
            continue;
        // Type check: multi_select should be array
        if (field.fieldType === 'multi_select' && !Array.isArray(value)) {
            issues.push({ field: field.id, message: 'Expected array for multi-select field' });
            continue;
        }
        // Type check: rank should be array
        if (field.fieldType === 'rank' && !Array.isArray(value)) {
            issues.push({ field: field.id, message: 'Expected array for rank field' });
            continue;
        }
        // Value validation: a custom predicate takes precedence over option lists.
        const isAllowed = field.validateValue
            ? field.validateValue
            : field.options && field.options.length > 0
                ? (() => {
                    const optionSet = new Set(field.options);
                    return (v) => optionSet.has(v);
                })()
                : null;
        if (isAllowed) {
            if (Array.isArray(value)) {
                const invalid = value.filter((v) => typeof v === 'string' && !isAllowed(v));
                if (invalid.length > 0) {
                    issues.push({
                        field: field.id,
                        message: `Invalid option(s): ${invalid.slice(0, 3).join(', ')}`,
                    });
                }
            }
            else if (typeof value === 'string' && !isAllowed(value)) {
                issues.push({
                    field: field.id,
                    message: `Invalid option: ${value}`,
                });
            }
        }
    }
    // Separate hard failures from warnings
    // Unknown fields are warnings; everything else is a real issue
    const hardIssues = issues.filter((i) => i.message !== 'Unknown field');
    return {
        valid: hardIssues.length === 0,
        issues,
    };
}
//# sourceMappingURL=business-brief-schema.js.map